'use client';

import React, { useState, useEffect, useCallback } from 'react';
import './sports-filter.css';
import { getWordPressArraysAction, getMatchCardsAction } from '@/lib/server-actions';

interface SportData {
    id: string;
    name: string;
    icon: string;
    filters: {
        now: number;
        hot: number;
        commentator: number;
        today: number;
        tomorrow: number;
        all: number;
    };
    leagues: League[];
}

interface League {
    id: string;
    name: string;
    image?: string;
    count: number;
    sportReal?: string;
}

interface FilterState {
    activeSport: string;
    activeFilter: string;
    activeLeague: string;
    isLeagueDropdownOpen: boolean;
    isSportDropdownOpen?: boolean;
    activeEsportType?: string;
}

interface SportsFilterProps {
  onFilterChange?: (matches: any[], counts: any) => void;
}

// Simple state for WordPress arrays
interface WordPressArrays {
  liveToday: any[];
  liveTomorrow: any[];
  isLive: any[];
  liveTops: any[];
  liveCaster: any[];
  liveAll: any[];
}

const SportsFilter: React.FC<SportsFilterProps> = ({ onFilterChange }) => {
    const [filterState, setFilterState] = useState<FilterState>({
        activeSport: 'football',
        activeFilter: 'all',
        activeLeague: 'all',
        isLeagueDropdownOpen: false,
        isSportDropdownOpen: false,
        activeEsportType: 'all'
    });

    // Simple state for WordPress data arrays
    const [wordpressArrays, setWordpressArrays] = useState<WordPressArrays>({
        liveToday: [],
        liveTomorrow: [],
        isLive: [],
        liveTops: [],
        liveCaster: [],
        liveAll: []
    });

    const [loading, setLoading] = useState(false);

    // Fetch WordPress arrays TRỰC TIẾP từ SQL - Server Action
    const fetchWordPressArrays = useCallback(async () => {
        setLoading(true);
        try {
            // Server Action - TRỰC TIẾP từ database, KHÔNG qua API
            const result = await getWordPressArraysAction();
            
            if (result.success && result.data) {
                setWordpressArrays(result.data);
            } else {
                console.error('❌ Server Action failed:', result.error);
            }

        } catch (error) {
            console.error('Failed to fetch WordPress arrays via Server Action:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch arrays on mount và mỗi 30s
    useEffect(() => {
        fetchWordPressArrays();
        const interval = setInterval(fetchWordPressArrays, 30000);
        return () => clearInterval(interval);
    }, [fetchWordPressArrays]);

    // Fetch match cards TRỰC TIẾP từ SQL - Server Action 
    const fetchMatchCards = useCallback(async () => {
        if (!onFilterChange) return;

        try {
            // Server Action - TRỰC TIẾP từ database, KHÔNG qua API
            const result = await getMatchCardsAction(
                filterState.activeFilter,
                filterState.activeSport,
                50
            );
            
            if (result.success && result.data) {
                // Send optimized matches to MatchList
                onFilterChange(result.data.matches, wordpressArrays);
            } else {
                console.error('❌ Server Action match cards failed:', result.error);
                // Fallback to array-based filtering
                onFilterChangeFallback();
            }

        } catch (error) {
            console.error('❌ Server Action match cards error:', error);
            // Fallback to array-based filtering
            onFilterChangeFallback();
        }
    }, [filterState.activeFilter, filterState.activeSport]); // Removed onFilterChange and wordpressArrays to prevent infinite loop

    // Fallback method - sử dụng arrays đã fetch (no dependencies to prevent loop)
    const onFilterChangeFallback = () => {
        if (!onFilterChange) return;

        let currentMatches: any[] = [];
        
        // Chọn mảng tương ứng với filter
        switch (filterState.activeFilter) {
            case 'now':
                currentMatches = wordpressArrays.isLive;
                break;
            case 'hot':
                currentMatches = wordpressArrays.liveTops;
                break;
            case 'commentator':
                currentMatches = wordpressArrays.liveCaster;
                break;
            case 'today':
                currentMatches = wordpressArrays.liveToday;
                break;
            case 'tomorrow':
                currentMatches = wordpressArrays.liveTomorrow;
                break;
            case 'all':
            default:
                currentMatches = [...wordpressArrays.liveToday, ...wordpressArrays.liveTomorrow];
                break;
        }

        // Filter theo sport nếu cần
        if (filterState.activeSport !== 'all') {
            const sportMap: { [key: string]: string } = {
                'football': '1',
                'basketball': '2', 
                'esports': '3',
                'tennis': '4',
                'badminton': '4',
                'volleyball': '4'
            };
            
            if (sportMap[filterState.activeSport]) {
                currentMatches = currentMatches.filter(match => 
                    match.match_type === sportMap[filterState.activeSport]
                );
            }
        }

        onFilterChange(currentMatches, wordpressArrays);
    };

    // Trigger optimized match cards fetch khi filter thay đổi
    useEffect(() => {
        // Ưu tiên fetch optimized match cards
        fetchMatchCards();
    }, [fetchMatchCards]);

    // Mock data - replace with actual data from props or API
    const sportsData: SportData[] = [
        {
            id: 'football',
            name: 'Bóng đá',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/football.svg',
            filters: { now: 10, hot: 43, commentator: 52, today: 62, tomorrow: 126, all: 571 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 571 },
                { id: 'epl', name: 'Ngoại Hạng Anh', image: 'https://imgts.api-football.xyz/football/competition/jednm9whz0ryox8/image/small', count: 10 },
                { id: 'laliga', name: 'La Liga', image: 'https://imgts.api-football.xyz/football/competition/vl7oqdehlyr510j/image/small', count: 7 },
                { id: 'bundesliga', name: 'Bundesliga', image: 'https://imgts.api-football.xyz/football/competition/gy0or5jhg6qwzv3/image/small', count: 6 },
                { id: 'seriea', name: 'Serie A', image: 'https://imgts.api-football.xyz/football/competition/4zp5rzghp5q82w1/image/small', count: 6 },
                { id: 'ligue1', name: 'Ligue 1', image: 'https://imgts.api-football.xyz/football/competition/yl5ergphnzr8k0o/image/small', count: 4 },
            ]
        },
        {
            id: 'basketball',
            name: 'Bóng rổ',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/basketball.svg',
            filters: { now: 0, hot: 0, commentator: 6, today: 4, tomorrow: 19, all: 55 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 55 },
                { id: 'cba', name: 'China Basketball Association Summer League', count: 10 },
                { id: 'nbl', name: 'China National Basketball League', count: 5 },
            ]
        },
        {
            id: 'tennis',
            name: 'Tennis',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/tennis.svg',
            filters: { now: 3, hot: 0, commentator: 3, today: 15, tomorrow: 14, all: 29 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 29 },
                { id: 'usopen', name: 'US Open Men Singles', count: 6 },
                { id: 'usopen-women', name: 'US Open Women Singles', count: 13 },
            ]
        },
        {
            id: 'badminton',
            name: 'Cầu lông',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/badminton.svg',
            filters: { now: 2, hot: 0, commentator: 0, today: 15, tomorrow: 11, all: 26 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 26 },
                { id: 'world-champ', name: 'World Championships, Singles', count: 7 },
                { id: 'world-champ-women', name: 'World Championships, Women Singles', count: 9 },
            ]
        },
        {
            id: 'volleyball',
            name: 'Bóng chuyền',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/volleyball.svg',
            filters: { now: 0, hot: 0, commentator: 2, today: 15, tomorrow: 14, all: 46 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 46 },
                { id: 'pan-american', name: 'Pan-American Cup', count: 18 },
                { id: 'world-champ-women', name: 'World Championship Women', count: 16 },
            ]
        },
        {
            id: 'esports',
            name: 'Esports',
            icon: 'https://10554877961.vws.vegacdn.vn/xoilacstatic/images/esports.svg',
            filters: { now: 2, hot: 0, commentator: 0, today: 15, tomorrow: 13, all: 28 },
            leagues: [
                { id: 'all', name: 'Tất cả giải đấu', count: 28 },
                { id: 'lec', name: 'LEC Summer 2025', sportReal: 'lol', count: 2 },
                { id: 'lpl', name: 'LPL Split 3 2025', sportReal: 'lol', count: 2 },
                { id: 'tcl', name: 'TCL Summer 2025', sportReal: 'lol', count: 4 },
            ]
        }
    ];

    const esportTypes = [
        { id: 'all', name: 'Tất cả các môn', count: 28 },
        { id: 'lol', name: 'LOL', count: 13 },
        { id: 'dota2', name: 'DOTA2', count: 4 },
        { id: 'csgo', name: 'CSGO', count: 11 }
    ];

    const activeSportData = sportsData.find(sport => sport.id === filterState.activeSport) || sportsData[0];

    const handleSportChange = (sportId: string) => {
        setFilterState(prev => ({
            ...prev,
            activeSport: sportId,
            activeFilter: 'all',
            activeLeague: 'all',
            isLeagueDropdownOpen: false,
            isSportDropdownOpen: false
        }));
    };

    const handleFilterChange = (filter: string) => {
        setFilterState(prev => ({
            ...prev,
            activeFilter: filter
        }));
    };

    const handleLeagueChange = (leagueId: string) => {
        setFilterState(prev => ({
            ...prev,
            activeLeague: leagueId,
            isLeagueDropdownOpen: false
        }));
    };

    const handleEsportTypeChange = (typeId: string) => {
        setFilterState(prev => ({
            ...prev,
            activeEsportType: typeId,
            isSportDropdownOpen: false
        }));
    };

    const toggleLeagueDropdown = () => {
        setFilterState(prev => ({
            ...prev,
            isLeagueDropdownOpen: !prev.isLeagueDropdownOpen,
            isSportDropdownOpen: false
        }));
    };

    const toggleSportDropdown = () => {
        setFilterState(prev => ({
            ...prev,
            isSportDropdownOpen: !prev.isSportDropdownOpen,
            isLeagueDropdownOpen: false
        }));
    };

    const getActiveLeagueName = () => {
        const league = activeSportData.leagues.find(l => l.id === filterState.activeLeague);
        return league?.name || 'Tất cả giải đấu';
    };

    const getActiveEsportTypeName = () => {
        const type = esportTypes.find(t => t.id === filterState.activeEsportType);
        return type?.name || 'Tất cả các môn';
    };

    // Simple function to get badge count = array.length
    const getBadgeCount = (filterType: string, sportId: string): number => {
        let targetArray: any[] = [];
        
        // Chọn mảng WordPress tương ứng
        switch (filterType) {
            case 'now':
                targetArray = wordpressArrays.isLive;
                break;
            case 'hot':
                targetArray = wordpressArrays.liveTops;
                break;
            case 'commentator':
                targetArray = wordpressArrays.liveCaster;
                break;
            case 'today':
                targetArray = wordpressArrays.liveToday;
                break;
            case 'tomorrow':
                targetArray = wordpressArrays.liveTomorrow;
                break;
            case 'all':
                targetArray = [...wordpressArrays.liveToday, ...wordpressArrays.liveTomorrow];
                break;
            default:
                return 0;
        }

        // Filter theo sport nếu cần
        if (sportId !== 'all') {
            const sportMap: { [key: string]: string } = {
                'football': '1',
                'basketball': '2', 
                'esports': '3',
                'tennis': '4',
                'badminton': '4',
                'volleyball': '4'
            };
            
            if (sportMap[sportId]) {
                targetArray = targetArray.filter(match => 
                    match.match_type === sportMap[sportId]
                );
            }
        }

        // BLV Xôi Lạc: count unique casters, not matches
        if (filterType === 'commentator') {
            const uniqueCasters = new Set(
                targetArray
                    .filter(match => match.caster && match.caster.trim() !== '')
                    .map(match => match.caster.trim())
            );
            return uniqueCasters.size;
        }

        // Default: return array length
        return targetArray.length;
    };

    return (
        <div className="main-page">
            <div className="page_bars mb-3">
                {/* Sports Navigation */}
                <div className="sports-bars">
                    <ul className="nav nav-tabs">
                        {sportsData.map((sport) => (
                            <li key={sport.id} className="nav-item" role="presentation">
                                <button
                                    className={`nav-link ${filterState.activeSport === sport.id ? 'active' : ''}`}
                                    onClick={() => handleSportChange(sport.id)}
                                    type="button"
                                    role="tab"
                                >
                                    <img width="auto" height="17" src={sport.icon} alt="sport icon" />
                                    <span>{sport.name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* LIVESCORE Button */}
                    <label className="btn-livescore">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M11.9356 10C11.9356 10.3953 11.8172 10.7628 11.6138 11.0692C11.2672 11.5913 10.6738 11.6992 10.0002 11.6992C8.93123 11.6992 8.35303 11.069 8.35303 10C8.35303 9.32561 8.40963 8.73174 8.93268 8.38526C9.23865 8.18256 9.60561 8.06451 10.0001 8.06451C11.0691 8.06455 11.9356 8.93108 11.9356 10Z" fill="#FE9801"></path>
                            <path d="M10.5443 11.3911C9.47531 11.3911 8.60879 10.5246 8.60879 9.45568C8.60879 9.06044 8.7291 8.69161 8.93246 8.38528C8.41031 8.73193 8.06445 9.32634 8.06445 10.0001C8.06445 11.069 8.93098 11.9355 9.99992 11.9355C10.6736 11.9355 11.2669 11.5913 11.6136 11.0692C11.3073 11.2725 10.9395 11.3911 10.5443 11.3911Z" fill="#FD6201"></path>
                            <path d="M3.3852 2.74005C3.55027 2.74005 3.71535 2.80306 3.84133 2.92907C3.84723 2.93497 3.85297 2.94095 3.85859 2.947L3.50258 3.84138C1.85758 5.48642 0.951563 7.67361 0.951563 10C0.951563 12.3264 1.85758 14.5136 3.50258 16.1586L3.85879 17.0528C3.85309 17.0589 3.84727 17.065 3.84133 17.0709C3.58961 17.323 3.18105 17.323 2.92902 17.0709C1.0402 15.1822 0 12.6711 0 10C0 7.32896 1.0402 4.81779 2.92906 2.92907C3.05492 2.80306 3.22012 2.74005 3.3852 2.74005Z" fill="#FFCB01"></path>
                            <path d="M7.03473 6.38959C7.19981 6.38959 7.36504 6.4526 7.49086 6.57861C7.50367 6.59142 7.51582 6.60459 7.52731 6.61818L7.08984 7.49088C5.70645 8.87443 5.70645 11.1256 7.08984 12.5092L7.52816 13.3809C7.51641 13.3948 7.50398 13.4083 7.49086 13.4215C7.23914 13.6735 6.83027 13.6735 6.57855 13.4215C4.69191 11.535 4.69191 8.4651 6.57855 6.57861C6.70441 6.45256 6.86965 6.38959 7.03473 6.38959Z" fill="#FE8101"></path>
                            <path d="M5.20998 4.56482C5.37506 4.56482 5.54014 4.62783 5.66611 4.75384C5.67346 4.76119 5.68057 4.76861 5.68748 4.77623L5.13689 5.66611C2.74748 8.05584 2.74748 11.9442 5.13689 14.3339L5.68795 15.2233C5.68092 15.2311 5.67365 15.2387 5.66615 15.2461C5.41412 15.4982 5.00588 15.4982 4.75385 15.2461C1.86135 12.3535 1.86135 7.64646 4.75385 4.75376C4.87982 4.62783 5.0449 4.56482 5.20998 4.56482Z" fill="#FFA801"></path>
                            <path d="M5.80664 10C5.80664 11.389 6.48812 12.6177 7.52836 13.3809C7.74281 13.1275 7.73012 12.7481 7.49105 12.5091C6.10766 11.1256 6.10766 8.87446 7.49105 7.49087C7.73012 7.25196 7.74195 6.87153 7.5275 6.61813C6.4873 7.38126 5.80664 8.61099 5.80664 10Z" fill="#FE7F01"></path>
                            <path d="M3.22559 10C3.22559 12.1018 4.18527 13.9808 5.68754 15.2233C5.9177 14.9702 5.91031 14.5783 5.66574 14.3339C3.27633 11.9442 3.27633 8.05586 5.66574 5.66614C5.91031 5.42172 5.91723 5.02934 5.68711 4.77621C4.1848 6.01871 3.22559 7.89821 3.22559 10Z" fill="#FEA101"></path>
                            <path d="M0.64502 10C0.64502 12.8146 1.8917 15.3378 3.85869 17.0528C4.09322 16.7998 4.08732 16.4046 3.84123 16.1587C2.19619 14.5136 1.29018 12.3264 1.29018 10C1.29018 7.67359 2.19619 5.4864 3.84123 3.84137C4.08732 3.59543 4.09303 3.19996 3.8585 2.94699C1.8915 4.66195 0.64502 7.18539 0.64502 10Z" fill="#FFA801"></path>
                            <path d="M16.6147 17.26C16.4496 17.26 16.2846 17.197 16.1586 17.0709C16.1527 17.0651 16.1469 17.0591 16.1413 17.053L16.4973 16.1586C18.1423 14.5136 19.0483 12.3264 19.0483 10C19.0483 7.67361 18.1423 5.48642 16.4973 3.84138L16.1411 2.94724C16.1468 2.94111 16.1526 2.93505 16.1586 2.92907C16.4103 2.67704 16.8188 2.67704 17.0709 2.92907C18.9597 4.81779 19.9999 7.32896 19.9999 10C19.9999 12.6711 18.9597 15.1822 17.0708 17.0709C16.945 17.197 16.7798 17.26 16.6147 17.26Z" fill="#FFCB01"></path>
                            <path d="M12.9651 13.6104C12.8 13.6104 12.6348 13.5474 12.509 13.4214C12.4962 13.4086 12.484 13.3954 12.4725 13.3818L12.91 12.5091C14.2934 11.1256 14.2934 8.87442 12.91 7.49082L12.4717 6.61907C12.4834 6.60516 12.4959 6.59164 12.509 6.57852C12.7607 6.32649 13.1696 6.32649 13.4213 6.57852C15.3079 8.465 15.3079 11.5349 13.4213 13.4214C13.2954 13.5475 13.1302 13.6104 12.9651 13.6104Z" fill="#FE8101"></path>
                            <path d="M14.79 15.4352C14.6249 15.4352 14.4598 15.3722 14.3338 15.2462C14.3265 15.2388 14.3194 15.2314 14.3125 15.2238L14.8631 14.3339C17.2525 11.9442 17.2525 8.05584 14.8631 5.66611L14.312 4.77669C14.319 4.76892 14.3263 4.7613 14.3338 4.75384C14.5858 4.50181 14.9941 4.50181 15.2461 4.75384C18.1386 7.6465 18.1386 12.3535 15.2461 15.2462C15.1201 15.3722 14.9551 15.4352 14.79 15.4352Z" fill="#FFA801"></path>
                            <path d="M14.1935 10C14.1935 8.61099 13.512 7.38224 12.4718 6.61911C12.2573 6.87251 12.27 7.25196 12.5091 7.49087C13.8925 8.87442 13.8925 11.1256 12.5091 12.5091C12.27 12.7481 12.2582 13.1285 12.4726 13.3819C13.5128 12.6188 14.1935 11.389 14.1935 10Z" fill="#FE7F01"></path>
                            <path d="M16.7743 10C16.7743 7.89823 15.8146 6.01924 14.3123 4.7767C14.0822 5.02983 14.0895 5.4217 14.3341 5.66612C16.7235 8.05584 16.7235 11.9442 14.3341 14.3339C14.0895 14.5783 14.0826 14.9707 14.3127 15.2238C15.815 13.9813 16.7743 12.1018 16.7743 10Z" fill="#FEA101"></path>
                            <path d="M19.355 10C19.355 7.18541 18.1083 4.6622 16.1413 2.9472C15.9068 3.20017 15.9127 3.59541 16.1588 3.84135C17.8038 5.48638 18.7098 7.67357 18.7098 9.99998C18.7098 12.3264 17.8038 14.5136 16.1588 16.1586C15.9127 16.4045 15.907 16.8 16.1415 17.053C18.1085 15.3381 19.355 12.8146 19.355 10Z" fill="#FFA801"></path>
                        </svg>
                        <span className="pc">LIVESCORE</span>
                        <span className="sp">LIVES</span>
                    </label>
                </div>

                {/* Filter Rows */}
                {sportsData.map((sport) => (
                    <div
                        key={sport.id}
                        className={`filter-row ${filterState.activeSport === sport.id ? 'active' : ''}`}
                        id={`filter-${sport.id}`}
                    >

                        {/* Filter Buttons */}
                        <ul className="list-filter">
                            <li className="live_indicator-bg">
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('now'); }}
                                    className={`now ${filterState.activeFilter === 'now' ? 'active' : ''}`}
                                    data-filter="now"
                                >
                                    <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/live-indicator.svg" alt="Live" />
                                    <span className="num">{loading ? '...' : getBadgeCount('now', sport.id)}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('hot'); }}
                                    className={`hot ${filterState.activeFilter === 'hot' ? 'active' : ''}`}
                                    data-filter="hot"
                                >
                                    <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/fire.svg" alt="Fire" />
                                    <span className="pc">Trận Hot</span>
                                    <span className="sp">Hot</span>
                                    <span className="num">{loading ? '...' : getBadgeCount('hot', sport.id)}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('commentator'); }}
                                    className={`commentator ${filterState.activeFilter === 'commentator' ? 'active' : ''}`}
                                    data-filter="commentator"
                                >
                                    <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/mic.svg" alt="Microphone" />
                                    <span className="pc">BLV Xôi Lạc</span>
                                    <span className="sp">BLV XL</span>
                                    <span className="num">{loading ? '...' : getBadgeCount('commentator', sport.id)}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('today'); }}
                                    className={`today ${filterState.activeFilter === 'today' ? 'active' : ''}`}
                                    data-filter="today"
                                >
                                    <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/calendar.svg" alt="Calendar" />
                                    Hôm nay <span className="num">{loading ? '...' : getBadgeCount('today', sport.id)}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('tomorrow'); }}
                                    className={`tomorrow ${filterState.activeFilter === 'tomorrow' ? 'active' : ''}`}
                                    data-filter="tomorrow"
                                >
                                    <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/calendar.svg" alt="Calendar" />
                                    Ngày mai <span className="num">{loading ? '...' : getBadgeCount('tomorrow', sport.id)}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleFilterChange('all'); }}
                                    className={`all ${filterState.activeFilter === 'all' ? 'active' : ''}`}
                                    data-filter="all"
                                >
                                    Tất cả <span className="num">{loading ? '...' : getBadgeCount('all', sport.id)}</span>
                                </a>
                            </li>
                        </ul>

                        {/* Dropdown Section */}
                        <div className="home-filter-dropdown">
                            {/* Esports Sport Type Dropdown */}
                            {sport.id === 'esports' && (
                                <div className="matches__filter--leagues">
                                    <div className="sl-leagues">
                                        <div
                                            className="custom-dropdown-trigger"
                                            onClick={toggleSportDropdown}
                                            data-sport="esports-sport"
                                        >
                                            <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/select-sport.svg" alt="league" />
                                            <span>{getActiveEsportTypeName()}</span>
                                        </div>
                                        <div className={`custom-dropdown-content ${filterState.isSportDropdownOpen ? 'show' : ''}`}>
                                            <div className="custom-dropdown-head">
                                                <span>Chọn môn</span>
                                                <svg
                                                    className="custom-dropdown-close"
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 14 14"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={toggleSportDropdown}
                                                >
                                                    <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </div>
                                            <div className="custom-dropdown-list">
                                                {esportTypes.map((type) => (
                                                    <div
                                                        key={type.id}
                                                        className={`custom-dropdown-option ${filterState.activeEsportType === type.id ? 'active' : ''}`}
                                                        onClick={() => handleEsportTypeChange(type.id)}
                                                    >
                                                        <div className="league-name">
                                                            <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/league.svg" alt="league" />
                                                            <span>{type.name}</span>
                                                        </div>
                                                        <span className="count">
                                                            <span className="count-num">{type.count}</span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" />
                                                                <text x="8" y="12" textAnchor="middle" fontSize="8" fill="currentColor">#{type.count}</text>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* League Dropdown */}
                            <div className="matches__filter--leagues">
                                <div className="sl-leagues">
                                    <div
                                        className="custom-dropdown-trigger"
                                        onClick={toggleLeagueDropdown}
                                        data-sport={sport.id}
                                    >
                                        <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/league.svg" alt="league" />
                                        <span>{getActiveLeagueName()}</span>
                                    </div>
                                    <div className={`custom-dropdown-content ${filterState.isLeagueDropdownOpen ? 'show' : ''}`}>
                                        <div className="custom-dropdown-head">
                                            <span>Chọn giải đấu</span>
                                            <svg
                                                className="custom-dropdown-close"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 14 14"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                onClick={toggleLeagueDropdown}
                                            >
                                                <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                        <div className="custom-dropdown-list">
                                            {activeSportData.leagues.map((league) => (
                                                <div
                                                    key={league.id}
                                                    className={`custom-dropdown-option ${filterState.activeLeague === league.id ? 'active' : ''}`}
                                                    onClick={() => handleLeagueChange(league.id)}
                                                >
                                                    <div className="league-name">
                                                        {league.image ? (
                                                            <img src={league.image} alt={league.name} />
                                                        ) : (
                                                            <img src="https://10554877961.vws.vegacdn.vn/xoilacstatic/images/league.svg" alt="league" />
                                                        )}
                                                        <span>{league.name}</span>
                                                    </div>
                                                    <span className="count">
                                                        <span className="count-num">{league.count}</span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" />
                                                            <text x="8" y="12" textAnchor="middle" fontSize="8" fill="currentColor">#{league.count}</text>
                                                        </svg>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SportsFilter;
